import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import Select from 'react-select';
import { useTranslate } from '../TranslationText';

/**
 * Clean select dropdown with React Hook Form integration.
 * Uses react-select directly (no NetaqInput wrapper).
 *
 * @param {string} name - Form field name
 * @param {string} label - Label text
 * @param {Array} options - Options array: [{ text: 'Label', value: 'id' }, ...]
 * @param {boolean} required - Show required asterisk
 * @param {boolean} disabled - Disable select
 * @param {object} rules - Validation rules
 * @param {boolean} isClearable - Allow clearing selection
 * @param {boolean} isMulti - Allow multiple selections
 * @param {boolean} hideLabel - Hide the label
 * @param {function} onChangeCallback - Optional callback after value changes
 * @param {string} placeholder - Select placeholder
 */
export default function FormSelect({
    name,
    label,
    options = [],
    required = false,
    disabled = false,
    rules,
    isClearable = false,
    isMulti = false,
    hideLabel = false,
    onChangeCallback,
    placeholder,
    containerClass = "mb-2",
    noValidate = false,
}) {
    const { control, formState: { errors } } = useFormContext();
    const { translate } = useTranslate();

    const validationRules = {
        ...(required && !noValidate && {
            required: translate('web.requiredField', 'Required Field'),
        }),
        ...rules,
    };

    // Normalize options to react-select format { label, value }
    const selectOptions = options.map((opt) => ({
        label: opt.text ?? opt.label ?? opt.name,
        value: opt.value ?? opt.id,
        ...opt,
    }));

    const fieldError = name.split('.').reduce((obj, key) => obj?.[key], errors);

    return (
        <div className={containerClass}>
            {!hideLabel && label && (
                <label htmlFor={name} className="capatalize_text">
                    {label}{' '}
                    {required && <i style={{ color: 'red', margin: 'auto 5px' }}>*</i>}
                </label>
            )}

            <Controller
                name={name}
                control={control}
                rules={validationRules}
                render={({ field }) => {
                    // Find selected option(s) from current value
                    const selectedValue = isMulti
                        ? selectOptions.filter((opt) =>
                            Array.isArray(field.value)
                                ? field.value.includes(opt.value)
                                : false
                        )
                        : selectOptions.find((opt) => opt.value === field.value) || null;

                    return (
                        <Select
                            inputId={name}
                            ref={field.ref}
                            options={selectOptions}
                            value={selectedValue}
                            onChange={(selected) => {
                                const newValue = isMulti
                                    ? (selected || []).map((s) => s.value)
                                    : selected?.value ?? null;
                                field.onChange(newValue);
                                if (onChangeCallback) onChangeCallback(selected);
                            }}
                            onBlur={field.onBlur}
                            isDisabled={disabled}
                            isClearable={isClearable}
                            isMulti={isMulti}
                            isSearchable
                            maxMenuHeight={175}
                            placeholder={placeholder ?? translate('web.select', 'Select')}
                            noOptionsMessage={() =>
                                translate('web.noOptions', 'No Options')
                            }
                            className={`max-hight-36${fieldError ? ' is-invalid' : ''}`}
                            styles={{
                                control: (base, state) => ({
                                    ...base,
                                    borderColor: state.isFocused ? '#86b7fe' : '#dee2e6', // Matches form-control border
                                    boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none',
                                    '&:hover': {
                                        borderColor: state.isFocused ? '#86b7fe' : '#dee2e6'
                                    },
                                    // Match Bootstrap's form-control:disabled exactly
                                    ...(state.isDisabled
                                        ? {
                                            backgroundColor: '#e9ecef',
                                            borderColor: '#cccccc',
                                            color: '#2c2c2a',
                                            opacity: 1 // react-select lowers opacity by default, preventing true color matching
                                        }
                                        : {
                                            backgroundColor: '#ffffff'
                                        }),
                                    ...(fieldError ? { borderColor: '#dc3545' } : {}),
                                }),
                                singleValue: (base, state) => ({
                                    ...base,
                                    color: '#2c2c2a', // Match form-control text color
                                    fontSize: '14px',
                                }),
                                placeholder: (base, state) => ({
                                    ...base,
                                    color: state.isDisabled ? '#2c2c2a' : '#6c757d', // Match placeholder color
                                }),
                                input: (base) => ({
                                    ...base,
                                    color: '#2c2c2a',
                                }),
                                dropdownIndicator: (base, state) => ({
                                    ...base,
                                    color: state.isDisabled ? '#adb5bd' : '#cccccc',
                                }),
                                indicatorSeparator: (base, state) => ({
                                    ...base,
                                    backgroundColor: state.isDisabled ? '#dee2e6' : '#cccccc',
                                })
                            }}
                        />
                    );
                }}
            />

            {fieldError && (
                <div className="text-danger" style={{ fontSize: 12, marginTop: 4 }}>
                    {fieldError.message || translate('web.requiredField', 'Required Field')}
                </div>
            )}
        </div>
    );
}
